import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MenuComponent } from '../../../shared/components/menu/menu.component';
import { LoginValidationComponent } from '../../../book-create/login-validation.component/login-validation.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { AdminUserListService } from '../../../shared/services/apis/platform/admin-user-list.service';
import { AdminUserListRequest } from '../../../shared/services/requests/platform/admin-user-list.request';
import { AdminUserExportItem, AdminUserIndicatorsResponse, AdminUserListItem } from '../../../shared/services/responses/platform/admin-user-list.response';
import { AdminUserDetailsDialogComponent } from './admin-user-details-dialog.component';
import { CsvExportService } from '../../../shared/services/csv-export.service';
import {
  PUBLICATION_BOOK_TYPE_OPTIONS,
  PUBLICATION_BUDGET_OPTIONS,
  PUBLICATION_TEXT_READY_OPTIONS,
  PUBLICATION_TIMEFRAME_OPTIONS,
  publicationProfileLabel
} from '../../../shared/constants/user-publication-profile.constants';

@Component({
  selector: 'app-admin-user-list',
  standalone: true,
  templateUrl: './admin-user-list.component.html',
  styleUrls: ['./admin-user-list.component.css'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MenuComponent,
    FooterComponent,
    LoginValidationComponent,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSlideToggleModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminUserListComponent implements OnInit {
  form!: FormGroup;
  isLoading = false;
  isLoadingMore = false;
  isExporting = false;
  list: AdminUserListItem[] = [];
  page = 1;
  readonly pageSize = 50;
  total = 0;
  indicators: AdminUserIndicatorsResponse | null = null;

  constructor(
    private fb: FormBuilder,
    private adminUserListService: AdminUserListService,
    private alertService: AlertService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private csv: CsvExportService
  ) { }

  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.form = this.fb.group({
      name: [''],
      email: [''],
      inclusionStart: [null],
      inclusionEnd: [null],
      profileStart: [null],
      profileEnd: [null],
      onlyBuyers: [false],
      onlyPublishers: [false],
    });
  }

  onLoginValidated(isLogged: boolean) {
    if (!isLogged) return;
    this.fetchIndicators();
    this.fetch(false);
  }

  fetchIndicators(): void {
    this.adminUserListService.indicators().subscribe({
      next: (res) => {
        if (!res.isError) {
          this.indicators = res;
        }
        this.cdr.markForCheck();
      },
      error: () => this.cdr.markForCheck(),
    });
  }

  fetch(append: boolean): void {
    if (append) {
      this.isLoadingMore = true;
    } else {
      this.isLoading = true;
    }

    const req = this.buildRequest(this.page);

    this.adminUserListService.list(req).subscribe({
      next: (res) => {
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription || 'Falha ao carregar usuários.', AlertType.error);
        } else {
          this.list = append ? [...this.list, ...(res.list ?? [])] : (res.list ?? []);
          this.total = res.total ?? this.list.length;
        }
        this.isLoading = false;
        this.isLoadingMore = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.alertService.show('Erro inesperado', err?.message || 'Falha ao carregar usuários.', AlertType.error);
        this.isLoading = false;
        this.isLoadingMore = false;
        this.cdr.markForCheck();
      },
    });
  }

  applyFilters(): void {
    this.page = 1;
    this.list = [];
    this.fetch(false);
  }

  resetFilters(): void {
    this.form.reset({ name: '', email: '', inclusionStart: null, inclusionEnd: null, profileStart: null, profileEnd: null, onlyBuyers: false, onlyPublishers: false });
    this.applyFilters();
  }

  loadMore(): void {
    if (!this.hasMore || this.isLoadingMore) return;
    this.page++;
    this.fetch(true);
  }

  get hasMore(): boolean {
    return this.list.length < this.total;
  }

  openDetails(idUser: number): void {
    this.adminUserListService.details(idUser).subscribe({
      next: (res) => {
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription || 'Falha ao carregar detalhes.', AlertType.error);
          this.cdr.markForCheck();
          return;
        }
        this.dialog.open(AdminUserDetailsDialogComponent, { width: '760px', maxWidth: '95vw', data: res });
      },
      error: (err) => {
        this.alertService.show('Erro inesperado', err?.message || 'Falha ao carregar detalhes.', AlertType.error);
        this.cdr.markForCheck();
      },
    });
  }

  bookTypeLabel(v?: number | null): string { return publicationProfileLabel(PUBLICATION_BOOK_TYPE_OPTIONS, v); }
  textReadyLabel(v?: number | null): string { return publicationProfileLabel(PUBLICATION_TEXT_READY_OPTIONS, v); }
  timeframeLabel(v?: number | null): string { return publicationProfileLabel(PUBLICATION_TIMEFRAME_OPTIONS, v); }
  budgetLabel(v?: number | null): string { return publicationProfileLabel(PUBLICATION_BUDGET_OPTIONS, v); }

  trackByUser(index: number, item: AdminUserListItem): number {
    return item.idUser;
  }

  downloadCsv(): void {
    if (this.isExporting) return;
    this.isExporting = true;

    this.adminUserListService.export(this.buildRequest(1)).subscribe({
      next: (res) => {
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription || 'Falha ao exportar usuários.', AlertType.error);
        } else {
          this.saveCsv(res.list ?? []);
        }
        this.isExporting = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.alertService.show('Erro inesperado', err?.message || 'Falha ao exportar usuários.', AlertType.error);
        this.isExporting = false;
        this.cdr.markForCheck();
      },
    });
  }

  private saveCsv(list: AdminUserExportItem[]): void {
    const header = [
      'ID', 'Nome', 'CPF', 'Telefone', 'Email', 'Ativo', 'Cadastrado em',
      'CEP', 'Rua', 'Número', 'Complemento', 'Bairro', 'Cidade', 'Estado', 'País',
      'Que livro quer publicar', 'Texto pronto', 'Quando quer publicar', 'Orçamento', 'Perfil preenchido em',
      'Livros publicados', 'Livros comprados', 'Última compra',
    ];

    const rows = list.map(u => [
      u.idUser,
      u.userName,
      u.userCpf,
      u.mobilePhone,
      u.userEmail,
      u.isActive ? 'Sim' : 'Não',
      this.csvDate(u.datetimeInclusion),
      u.zipcode,
      u.street,
      u.addressNumber,
      u.complement,
      u.neighborhood,
      u.city,
      u.state,
      u.country,
      this.bookTypeLabel(u.publicationBookType),
      this.textReadyLabel(u.publicationTextReady),
      this.timeframeLabel(u.publicationTimeframe),
      this.budgetLabel(u.publicationBudget),
      this.csvDate(u.datetimePublicationProfile),
      u.publishedBooksCount,
      u.purchasedBooksCount,
      this.csvDate(u.lastPurchaseDate),
    ]);

    // Separador ";", BOM UTF-8 e datas dd/MM/yyyy HH:mm (CsvExportService)
    this.csv.download(this.csv.fileName('usuarios'), header, rows);
  }

  private csvDate(value?: string | null): string {
    return this.csv.date(value);
  }

  private buildRequest(page: number): AdminUserListRequest {
    return {
      page,
      pageSize: this.pageSize,
      name: (this.form.value.name || '').trim() || null,
      email: (this.form.value.email || '').trim() || null,
      inclusionStart: this.toIsoOrNull(this.form.value.inclusionStart),
      inclusionEnd: this.toIsoOrNull(this.form.value.inclusionEnd),
      publicationProfileStart: this.toIsoOrNull(this.form.value.profileStart),
      publicationProfileEnd: this.toIsoOrNull(this.form.value.profileEnd),
      onlyBuyers: !!this.form.value.onlyBuyers,
      onlyPublishers: !!this.form.value.onlyPublishers,
    };
  }

  private toIsoOrNull(date: Date | null): string | null {
    if (!date) return null;
    return new Date(date).toISOString();
  }
}
